/**
 * Tillbaka-navigering som landar på rätt KUND, inte bara rätt flik.
 *
 * Tillbaka-knapparna i estimat, kalkyl och offert navigerade till
 * '/workspace/upphandling' respektive '/workspace/projekt' utan att säga vem man
 * kom ifrån. Man landade högst upp i en lång lista och fick leta rätt på kunden
 * igen. Mekanismen för att peka ut kunden fanns redan — Pipeline skickar
 * highlightProcurementId — men bara Pipeline använde den.
 */

/**
 * Vart Tillbaka ska leda från en kundvy.
 *
 * Har upphandlingen blivit ett projekt landar man i Projektering på just det
 * projektet; annars i Upphandling med kunden markerad. Saknas projektreferensen
 * faller vi tillbaka på projektlistan.
 *
 * @param {Object} procurement - Måste ha status; convertedProjectRef när den finns
 * @returns {{ pathname: string, state: Object }}
 */
export const tillbakaTillKund = (procurement) => {
  const arProjekt = procurement?.status === 'Projekt';

  if (arProjekt) {
    // convertedProjectRef kan vara ett id eller ett populerat dokument
    const projektId = procurement?.convertedProjectRef?._id || procurement?.convertedProjectRef;

    /**
     * Finns projektet går vi rakt in i Projektering i stället för till
     * projektlistan. Man kom ju därifrån — att landa i listan och behöva klicka
     * sig in på samma projekt igen är ett steg för mycket.
     *
     * Ingen flik anges: index-routen kör DefaultSubTabRedirect, som väljer
     * rollens första flik (Kanban för BK, IA och admin). Så slipper vi anta en
     * flik som rollen kanske inte har.
     *
     * useDeepLinking i ProjekteringSection läser projectId ur URL:en och
     * markerar projektet i ProjectContext, så vyn öppnas på rätt kund.
     */
    if (projektId) {
      return { pathname: `/workspace/projektering/${String(projektId)}`, state: {} };
    }

    // Saknas referensen vet vi inte vilket projekt det blev — då är listan,
    // med kunden markerad om möjligt, det bästa vi kan göra.
    return {
      pathname: '/workspace/projekt',
      state: {}
    };
  }

  return {
    pathname: '/workspace/upphandling',
    // ProcurementDashboard matchar på procurement._id ELLER leadId
    state: procurement?._id ? { highlightProcurementId: String(procurement._id) } : {}
  };
};

/**
 * Navigera till en destination som kan vara antingen en ren sökväg (äldre
 * anropare) eller ett objekt med state.
 *
 * @param {Function} navigate - från useNavigate
 * @param {string|Object} destination
 */
export const navigeraTillbaka = (navigate, destination) => {
  if (!destination) return;
  if (typeof destination === 'string') {
    navigate(destination);
    return;
  }
  navigate(destination.pathname, { state: destination.state || {} });
};
